import axios, {
	type AxiosError,
	type AxiosInstance,
	type InternalAxiosRequestConfig
} from "axios";
import { authRefreshToken } from "../services/auth.service";

export const backendUrl = import.meta.env.VITE_BE_URL || "";

const SESSION_KEYS = ["accessToken", "refreshToken", "idToken", "tokenExpiration"];

let signingOut = false;

export const endSession = () => {
	if (signingOut) return;
	SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
	const { pathname, search } = window.location;
	// Only latch once a redirect is under way, so a later in-app sign-in can still end its session.
	if (pathname.includes("/login")) return;
	signingOut = true;
	// Reports keep their table style and section in the query string.
	window.location.href = `/login?redirect=${encodeURIComponent(pathname + search)}`;
};

const isTokenExpired = () => {
	let localStorageExpirationTime = localStorage.getItem("tokenExpiration");

	if (!localStorageExpirationTime) {
		console.log("Token does not exist in local storage");
		return true;
	} else {
		const expirationTime = new Date(localStorageExpirationTime).toISOString();
		const currentUtcTime = new Date().toISOString();
		return currentUtcTime > expirationTime;
	}
};

// A refresh token is single use: the BE deletes the row as soon as it is spent.
// Each request builds its own axios instance, so a page that fires several calls
// at once would otherwise have every one of them see the expired token and post
// the same refresh token in parallel — one wins, the rest are rejected and send
// the user to the login screen mid-session. One refresh is shared by everyone
// waiting on it instead.
let refreshInFlight: Promise<any> | null = null;

const requestAccessToken = async () => {
	const refreshToken = localStorage.getItem("refreshToken");
	try {
		const response = await authRefreshToken(refreshToken);
		const accessToken = response.data.accessToken;
		const expirationTime = response.data.expiresOn; // Token expiration time
		const idToken = response.data.idToken;
		const newRefreshToken = response.data.refreshToken;

		localStorage.setItem("accessToken", accessToken);
		localStorage.setItem("idToken", idToken);
		localStorage.setItem("refreshToken", newRefreshToken);
		localStorage.setItem("tokenExpiration", expirationTime);

		return accessToken; // Return the refreshed token
	} catch (error) {
		console.error("Token refresh failed:", error);
		// Only a refused refresh (400, or 401) ends the session. A 429, 5xx or network error
		// keeps the tokens and the next request refreshes again; nothing is retried automatically.
		if (isRefusedRefresh(error)) {
			// Another tab may have spent this token first, and the tokens it stored are still good.
			if (localStorage.getItem("refreshToken") === refreshToken) endSession();
		}
		throw error;
	}
};

export const isRefusedRefresh = (error: unknown): boolean => {
	const status = (error as { response?: { status?: number } } | null)?.response
		?.status;
	return status === 400 || status === 401;
};

const refreshAccessToken = async () => {
	if (refreshInFlight) return refreshInFlight;

	refreshInFlight = requestAccessToken();
	try {
		return await refreshInFlight;
	} finally {
		refreshInFlight = null;
	}
};

// Their 401 means bad credentials or a spent token, not an ended session, and retrying them could loop.
const AUTH_ENDPOINTS = ["api/auth/login", "api/auth/refresh", "api/auth/logout", "api/auth/sso/"];

const isAuthEndpoint = (url?: string) =>
	!!url && AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

// The server can end a session before the local expiry: an organisation switch, deactivation or revocation.
const handleUnauthorized = async (api: AxiosInstance, error: AxiosError) => {
	const config = error.config as RetriableConfig | undefined;
	if (error.response?.status !== 401 || !config || isAuthEndpoint(config.url) || signingOut) {
		return Promise.reject(error);
	}

	if (!config._retried) {
		config._retried = true;
		try {
			await refreshAccessToken();
		} catch {
			// A refused refresh has already ended the session; a 429, 5xx or network error keeps it.
			return Promise.reject(error);
		}
		return api.request(config);
	}

	endSession();
	return Promise.reject(error);
};

/**
 * `baseUrl` defaults to the BE, so every existing caller is unchanged. It is a
 * parameter only so that a service talking to a *different* backend — today the
 * observability dashboard, which reads the shared functions app — gets the same
 * Authorization header and, more importantly, the same single-flight refresh
 * above. A second copy of this interceptor would mean a second `refreshInFlight`
 * and the parallel-refresh bug it exists to prevent.
 */
export const axiosRequestInterceptor = (baseUrl: string = backendUrl) => {
	const api = axios.create({
		baseURL: baseUrl, // API base URL
		// Cross-origin either way (the BE has never been same-origin with the SPA),
		// so this is not new here. Both backends must answer with an explicit
		// Access-Control-Allow-Origin plus Allow-Credentials: true — never "*".
		withCredentials: true,
	});

	api.interceptors.request.use(
		async (config) => {
			if (isTokenExpired()) {
				try {
					const newAccessToken = await refreshAccessToken(); // Wait for token refresh

					config.headers.Authorization = `Bearer ${newAccessToken}`; // Use the new token
				} catch (error) {
					return Promise.reject(error); // If refresh fails, reject the request
				}
			} else {
				const accessToken = localStorage.getItem("accessToken");
				if (accessToken) {
					config.headers.Authorization = `Bearer ${accessToken}`;
				}
			}
			config.headers.Accept = "application/json"; // Set other headers

			return config;
		},
		(error) => {
			return Promise.reject(error);
		}
	);

	// The functions app answers 401 while its auth settings are missing, which says nothing about the session.
	if (baseUrl === backendUrl) {
		api.interceptors.response.use(
			(response) => response,
			(error) => handleUnauthorized(api, error)
		);
	}

	return api;
};
